import React, { useEffect, useMemo, useState } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { getPreferredFormattedTimeSeconds } from "../../../utils/preferences";
import { getTimeRemainingValues } from "../../../lib/race";

const HEADERS = [
  { key: "trackState", label: "Track State", width: 100 },
  { key: "timeRemainingRace", label: "tRem", width: 40 },
  { key: "timeRemainingMaximum", label: "tRemMax", width: 60 },
  { key: "timeRemaining-2", label: "tRem-2", width: 60 },
] as const;

const FLAG_LABELS: Record<string, string> = {
  RED: "Red Flag",
  FULL_YELLOW: "FCY",
  SAFETY_CAR: "Safety Car",
  GREEN: "Green Flag",
  YELLOW: "Yellow Flag",
  NONE: "-",
};

const FLAG_COLOURS: Record<string, string> = {
  RED: "red",
  FULL_YELLOW: "yellow",
  SAFETY_CAR: "orange",
  GREEN: "green",
  YELLOW: "yellow",
};

export default function TimeRemainingTable({
  carColour,
  carNumber,
}: {
  carColour: string;
  carNumber?: string;
}) {
  const sessionStartTime = useSelector(
    (state: RootState) =>
      state.aks?.v2?.timing?.session?.status?.sessionStartTime,
  );

  const strategyImport = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data,
    isEqual,
  );

  const activeStandings = useSelector(
    (state: RootState) =>
      state.aks?.v2?.timing?.session?.standings?.overall?.active,
    isEqual,
  );

  const interventionsData = useSelector(
    (state: RootState) => state.interventions,
    isEqual,
  );

  const currentFlag = useSelector(
    (state: RootState) => state.aks?.v2?.timing?.session?.status?.currentFlag,
  );

  const lapTableData = useSelector(
    (state: RootState) =>
      (carColour ? state.raceData[carColour]?.lapTable : undefined) ?? {},
    isEqual,
  );

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const { timeRemainingRace, timeRemainingMaximum, timeRemaining2 } =
    useMemo(() => {
      const tRaceMaximum = strategyImport?.tRaceMaximum;
      const tRaceMaximumWithSuspension =
        strategyImport?.tRaceMaximumWithSuspension;

      const finishFlagTime = interventionsData.flagPeriods?.find(
        (period) => period.type === "FINISH",
      )?.startTime;
      const effectiveNow = finishFlagTime ?? now;

      const stoppedSeconds = (interventionsData.flagPeriods ?? [])
        .filter((period) => period.type === "RED")
        .reduce((total, period) => {
          const endTime = period.endTime ?? effectiveNow;
          return total + (endTime - period.startTime) / 1000;
        }, 0);

      const elapsedTime =
        sessionStartTime != null
          ? (effectiveNow - sessionStartTime) / 1000
          : null;

      const timeRemainingRace =
        tRaceMaximum != null && elapsedTime != null
          ? tRaceMaximum + stoppedSeconds - elapsedTime
          : null;

      const timeRemainingMaximum =
        tRaceMaximumWithSuspension != null && elapsedTime != null
          ? tRaceMaximumWithSuspension - elapsedTime
          : null;

      const lapNumbers = Object.keys(lapTableData).map(Number);
      const thirdToLastLapNumber = lapNumbers[lapNumbers.length - 3];
      const lapTimeRemaining2 =
        thirdToLastLapNumber != null
          ? getTimeRemainingValues(
              lapTableData,
              thirdToLastLapNumber,
              tRaceMaximum,
              tRaceMaximumWithSuspension,
              interventionsData.flagPeriods,
            ).race
          : null;

      const driverEntry = Object.values(activeStandings ?? {}).find(
        (entry) => String(entry.participant) === String(carNumber),
      );
      const gapFirstSeconds =
        driverEntry?.gapFirstTime != null
          ? driverEntry.gapFirstTime / 1000
          : null;

      const timeRemaining2 =
        lapTimeRemaining2 != null && gapFirstSeconds != null
          ? lapTimeRemaining2 - gapFirstSeconds
          : lapTimeRemaining2;

      return { timeRemainingRace, timeRemainingMaximum, timeRemaining2 };
    }, [
      now,
      sessionStartTime,
      strategyImport,
      lapTableData,
      carNumber,
      activeStandings,
      interventionsData,
    ]);

  return (
    <div className={styles.container}>
      <table>
        <colgroup>
          {HEADERS.map((header) => (
            <col key={header.key} style={{ width: `${header.width}px` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {HEADERS.map((header) => (
              <th key={header.key}>{header.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              style={{
                backgroundColor: currentFlag
                  ? FLAG_COLOURS[currentFlag]
                  : undefined,
                color: "black",
              }}
            >
              {currentFlag ? FLAG_LABELS[currentFlag] ?? currentFlag : "-"}
            </td>
            <td>
              {timeRemainingRace != null
                ? getPreferredFormattedTimeSeconds(timeRemainingRace, true, 0)
                : "-"}
            </td>
            <td>
              {timeRemainingMaximum != null
                ? getPreferredFormattedTimeSeconds(
                    timeRemainingMaximum,
                    true,
                    0,
                  )
                : "-"}
            </td>
            <td>
              {timeRemaining2 != null
                ? getPreferredFormattedTimeSeconds(timeRemaining2, true, 0)
                : "-"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
